import { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Camera, CheckCircle, Lightbulb, ListChecks, Question, Spinner,
  Warning as WarningIcon, MagnifyingGlass, ArrowSquareOut, Plus,
} from "@phosphor-icons/react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const ASSESSMENT_META = {
  looks_good: { label: "Looks good", color: "#6A8C69", bg: "#EEF5EE", icon: CheckCircle, weight: "fill" },
  issues_found: { label: "Issues found", color: "#C14953", bg: "#FBEDEE", icon: WarningIcon, weight: "fill" },
  uncertain: { label: "Hard to tell", color: "#8B7355", bg: "#F0EDE6", icon: Question, weight: "duotone" },
  advice: { label: "Advice", color: "#2C4251", bg: "#EDF1F4", icon: Lightbulb, weight: "fill" },
};

const MODE_META = {
  need_help: {
    title: "Need help",
    description: "Ask anything about this step. The AI sees your whole project.",
    placeholder: "Type your question…",
    needsPhoto: false,
  },
  check_my_work: {
    title: "Check my work",
    description: "Snap a photo and the AI checks if it looks right.",
    placeholder: "Anything you want to flag? (optional)",
    needsPhoto: true,
  },
  check_shopping_list: {
    title: "Final Materials Review",
    description: "AI reviews your plan and flags likely missing items before you head to the hardware store.",
    placeholder: "",
    needsPhoto: false,
  },
};

// Phase-aware overrides — when a project is complete (state=complete OR all steps done),
// re-frame "Check my work" as a final quality/safety pass so the user knows the AI is
// looking for finish-quality issues, not for missed prep steps.
function resolveMeta(mode, project) {
  const base = MODE_META[mode] || MODE_META.need_help;
  if (mode !== "check_my_work") return base;
  const isComplete =
    project?.state === "complete" ||
    project?.status === "done" ||
    (project?.step_count > 0 && project?.steps_done >= project?.step_count);
  if (isComplete) {
    return {
      ...base,
      title: "Final check",
      description: "Upload photos of the finished job and the AI will check for anything obvious before you call it done.",
      placeholder: "Anything you want the AI to look at closely? (optional)",
    };
  }
  return base;
}

export default function HelpSheet({
  open, onOpenChange, projectId, stepId, mode, onApplied, project,
}) {
  const [phase, setPhase] = useState("ask"); // ask | loading | result
  const [question, setQuestion] = useState("");
  const [photos, setPhotos] = useState([]); // array of upload records, max 5
  const [photoUploading, setPhotoUploading] = useState(false);
  const [helpRecord, setHelpRecord] = useState(null);
  const [pickStep, setPickStep] = useState({});      // index -> bool
  const [pickMaterial, setPickMaterial] = useState({});
  const [pickBlock, setPickBlock] = useState(false);
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);
  const meta = resolveMeta(mode, project);
  const MAX_PHOTOS = 5;

  useEffect(() => {
    if (open) {
      setPhase(mode === "check_shopping_list" ? "loading" : "ask");
      setQuestion(""); setPhotos([]); setHelpRecord(null);
      setPickStep({}); setPickMaterial({}); setPickBlock(false);
      if (mode === "check_shopping_list") triggerShoppingList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const triggerShoppingList = async () => {
    try {
      const r = await api.post(`/projects/${projectId}/check-shopping-list`);
      setHelpRecord(r.data);
      setPhase("result");
    } catch (e) {
      toast.error("Could not check shopping list");
      onOpenChange(false);
    }
  };

  const ask = async () => {
    if (mode === "check_my_work" && photos.length === 0) {
      toast.error("Add at least one photo so the AI can check your work");
      return;
    }
    setPhase("loading");
    try {
      let res;
      if (mode === "need_help") {
        res = await api.post(`/projects/${projectId}/steps/${stepId}/help`, {
          question: question.trim(),
        });
      } else {
        res = await api.post(`/projects/${projectId}/steps/${stepId}/check-my-work`, {
          photo_ids: photos.map((p) => p.id),
          question: question.trim(),
        });
      }
      setHelpRecord(res.data);
      setPhase("result");
    } catch (e) {
      toast.error("AI request failed. Try again.");
      setPhase("ask");
    }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    const slice = files.slice(0, room);
    if (files.length > room) {
      toast.warning(`Only ${MAX_PHOTOS} photos allowed — keeping the first ${room}.`);
    }
    setPhotoUploading(true);
    try {
      for (const file of slice) {
        if (file.size > 12 * 1024 * 1024) {
          toast.error(`${file.name} is too large (12MB max) — skipping`);
          continue;
        }
        const form = new FormData();
        form.append("file", file);
        const r = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, r.data]);
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setPhotoUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const apply = async () => {
    const steps = Object.entries(pickStep).filter(([, v]) => v).map(([k]) => Number(k));
    const materials = Object.entries(pickMaterial).filter(([, v]) => v).map(([k]) => Number(k));
    if (steps.length === 0 && materials.length === 0 && !pickBlock) {
      onOpenChange(false);
      return;
    }
    try {
      setApplying(true);
      const r = await api.post(`/projects/help/${helpRecord.id}/apply`, {
        add_step_indexes: steps,
        add_material_indexes: materials,
        block_step: pickBlock,
      });
      toast.success("Project updated");
      onApplied?.(r.data.project);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  const ai = helpRecord?.ai_response;
  const A = ai ? (ASSESSMENT_META[ai.assessment] || ASSESSMENT_META.advice) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="help-sheet"
        className="bg-chalk border-t-2 border-stone_border rounded-t-3xl p-0 max-h-[92vh] overflow-y-auto"
      >
        <div className="mx-auto w-12 h-1.5 bg-stone_border rounded-full my-3" />
        <SheetHeader className="px-6">
          <SheetTitle className="font-heading text-2xl text-left">{meta.title}</SheetTitle>
          <SheetDescription className="text-left text-sm text-timber-700">
            {meta.description}
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 py-4 pb-8 max-w-md mx-auto w-full">
          {phase === "ask" && (
            <div className="space-y-3">
              {meta.needsPhoto && (
                <div>
                  <p className="label-cap">Photos of your work <span className="text-timber-500 font-normal normal-case tracking-normal">· up to {MAX_PHOTOS}</span></p>
                  <div className="mt-1 grid grid-cols-3 gap-2" data-testid="help-photo-grid">
                    {photos.map((ph) => (
                      <div key={ph.id} className="relative" data-testid={`help-photo-${ph.id}`}>
                        <img
                          src={fileUrl(ph.id)}
                          alt=""
                          className="w-full aspect-square object-cover rounded-xl border-2 border-stone_border"
                        />
                        <button
                          data-testid={`help-photo-remove-${ph.id}`}
                          onClick={() => removePhoto(ph.id)}
                          className="absolute -top-1.5 -right-1.5 bg-block text-white rounded-full w-6 h-6 text-xs font-bold flex items-center justify-center shadow"
                          aria-label="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        data-testid="help-photo-trigger"
                        onClick={() => fileRef.current?.click()}
                        disabled={photoUploading}
                        className="w-full aspect-square rounded-xl border-2 border-dashed border-stone_border bg-kraft flex flex-col items-center justify-center gap-1 text-timber-700 hover:border-masking hover:text-masking transition"
                      >
                        <Camera size={24} weight="duotone" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-center px-1">
                          {photoUploading ? "Uploading…" : photos.length === 0 ? "Add photo" : "Add more"}
                        </span>
                      </button>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={uploadPhoto}
                      className="hidden"
                      data-testid="help-photo-input"
                    />
                  </div>
                </div>
              )}
              <div>
                <p className="label-cap">{meta.needsPhoto ? "Anything to add?" : "Your question"}</p>
                {mode === "need_help" && project?.ai_meta?.beginner_questions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1 mb-1" data-testid="help-suggested-questions">
                    {project.ai_meta.beginner_questions.slice(0, 5).map((q, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`help-suggested-${i}`}
                        onClick={() => setQuestion(q)}
                        className="text-xs text-denim bg-chalk border border-stone_border rounded-full px-2.5 py-1 font-semibold hover:border-masking hover:text-masking transition"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                <Textarea
                  data-testid="help-question-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={3}
                  placeholder={meta.placeholder}
                  className="mt-1 bg-chalk border-2 border-stone_border rounded-xl text-base"
                />
              </div>
              <Button
                data-testid="help-ask-btn"
                onClick={ask}
                className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[60px] text-base font-bold btn-press"
              >
                <MagnifyingGlass size={20} weight="bold" className="mr-2" />
                Ask the copilot
              </Button>
            </div>
          )}

          {phase === "loading" && (
            <div className="flex flex-col items-center py-12 gap-3" data-testid="help-loading">
              <Spinner size={40} className="text-masking animate-spin" />
              <p className="text-timber-700 font-semibold">Thinking…</p>
              <p className="text-xs text-timber-500">Reading your project context</p>
            </div>
          )}

          {phase === "result" && ai && (
            <div className="space-y-4" data-testid="help-result">
              {/* Brand-aligned scope/safety refusal — supersedes the normal layout. */}
              {ai.refusal_kind ? (
                <div
                  data-testid={`help-refusal-${ai.refusal_kind}`}
                  className="border-2 border-timber-900 bg-kraft rounded-xl p-5"
                >
                  <p
                    className="font-heading text-xl font-bold text-timber-900 leading-tight"
                    data-testid="help-refusal-title"
                  >
                    {`That one's not in our tool belt.`}
                  </p>
                  <p
                    className="text-sm text-timber-700 mt-3 leading-relaxed whitespace-pre-line"
                    data-testid="help-refusal-message"
                  >
                    {ai.refusal_kind === "safety_refusal"
                      ? `Half Job can't help with illegal, dangerous or regulated work.\n\nIf you're unsure, stop and seek advice from a qualified professional.`
                      : `Half Job can only help with home projects, project planning, materials, shopping, troubleshooting and finishing the projects you're working on.\n\nIf you're working on something else, we'd love to hear about it through Future Requests.`}
                  </p>
                </div>
              ) : (
              <>
              {/* Assessment banner */}
              <div
                className="border-2 rounded-xl p-3 flex items-start gap-2"
                style={{ borderColor: A.color, backgroundColor: A.bg }}
              >
                <A.icon size={22} weight={A.weight} color={A.color} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span
                      className="text-[11px] font-bold uppercase tracking-wider"
                      style={{ color: A.color }}
                    >
                      {A.label}
                    </span>
                    {ai.confidence != null && (
                      <span className="text-xs font-mono font-bold" style={{ color: A.color }}>
                        {Math.round(ai.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  {ai.observation && (
                    <p className="text-sm text-timber-900 mt-1">{ai.observation}</p>
                  )}
                </div>
              </div>

              {ai.recommended_action && (
                <div>
                  <p className="label-cap">What to do next</p>
                  <p className="font-heading text-lg font-bold text-timber-900 leading-snug mt-1">
                    {ai.recommended_action}
                  </p>
                </div>
              )}

              {ai.tips?.length > 0 && (
                <div>
                  <p className="label-cap flex items-center gap-1"><Lightbulb size={12} weight="fill" /> Tips</p>
                  <ul className="mt-1 text-sm text-timber-900 list-disc pl-5 space-y-1">
                    {ai.tips.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}

              {ai.safety?.length > 0 && (
                <div className="bg-[#FBEDEE] border-2 border-block rounded-xl p-3">
                  <p className="label-cap text-block flex items-center gap-1"><WarningIcon size={12} weight="fill" /> Safety</p>
                  <ul className="mt-1 text-sm text-timber-900 list-disc pl-5 space-y-1">
                    {ai.safety.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </div>
              )}

              {ai.related_searches?.length > 0 && (
                <div>
                  <p className="label-cap">Watch & learn</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ai.related_searches.map((q, i) => (
                      <a
                        key={i}
                        href={`https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`help-search-${i}`}
                        className="text-xs font-semibold text-denim bg-chalk border border-stone_border rounded-full px-2.5 py-1 flex items-center gap-1 hover:border-masking hover:text-masking transition"
                      >
                        {q} <ArrowSquareOut size={11} weight="bold" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Diff preview / explicit approval */}
              {(ai.suggested_new_steps?.length > 0 || ai.suggested_new_materials?.length > 0 || ai.suggest_block_step) && (
                <div className="border-2 border-denim rounded-xl p-3 bg-denim-50">
                  <p className="label-cap text-denim flex items-center gap-1">
                    <ListChecks size={14} weight="fill" /> AI suggests these changes
                  </p>
                  <p className="text-[11px] text-timber-700 mt-0.5">
                    Tick what you want to apply. Nothing is added without your approval.
                  </p>

                  {ai.suggest_block_step && stepId && (
                    <label className="flex items-start gap-2 mt-3 cursor-pointer" data-testid="apply-block-step">
                      <Checkbox
                        checked={pickBlock}
                        onCheckedChange={(v) => setPickBlock(!!v)}
                        className="mt-0.5 border-2 border-block data-[state=checked]:bg-block data-[state=checked]:text-white"
                      />
                      <span className="text-sm font-semibold text-timber-900">
                        Mark current step as <span className="text-block">blocked</span>
                      </span>
                    </label>
                  )}

                  {ai.suggested_new_steps?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-timber-700">New step(s) to add</p>
                      <div className="space-y-1.5 mt-1">
                        {ai.suggested_new_steps.map((s, i) => (
                          <label
                            key={i}
                            className="flex items-start gap-2 cursor-pointer bg-chalk rounded-lg p-2 border border-stone_border"
                            data-testid={`apply-step-${i}`}
                          >
                            <Checkbox
                              checked={!!pickStep[i]}
                              onCheckedChange={(v) => setPickStep((p) => ({ ...p, [i]: !!v }))}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-timber-900">{s.title}</p>
                              {s.notes && <p className="text-xs text-timber-700">{s.notes}</p>}
                            </div>
                            <Plus size={14} className="text-denim flex-shrink-0 mt-1" />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {ai.suggested_new_materials?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-timber-700">New material(s) to add</p>
                      <div className="space-y-1.5 mt-1">
                        {ai.suggested_new_materials.map((m, i) => (
                          <label
                            key={i}
                            className="flex items-start gap-2 cursor-pointer bg-chalk rounded-lg p-2 border border-stone_border"
                            data-testid={`apply-material-${i}`}
                          >
                            <Checkbox
                              checked={!!pickMaterial[i]}
                              onCheckedChange={(v) => setPickMaterial((p) => ({ ...p, [i]: !!v }))}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-timber-900">
                                {m.name}
                                {m.quantity && <span className="text-timber-500 font-normal"> · {m.quantity}</span>}
                              </p>
                              {m.notes && <p className="text-xs text-timber-700">{m.notes}</p>}
                              <span className="text-[10px] font-bold uppercase tracking-wider text-masking">
                                {m.status === "need_to_buy" ? "Need to buy" : m.status}
                              </span>
                            </div>
                            <Plus size={14} className="text-denim flex-shrink-0 mt-1" />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    data-testid="help-apply-btn"
                    onClick={apply}
                    disabled={applying}
                    className="w-full mt-4 bg-denim text-chalk hover:bg-denim-600 min-h-[52px] font-bold"
                  >
                    {applying ? "Applying…" : "Apply selected changes"}
                  </Button>
                </div>
              )}
              </>
              )}

              <Button
                data-testid="help-close-btn"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full border-2 border-stone_border min-h-[48px]"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
